import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
    IonModal,
    IonContent,
    IonSearchbar,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonButton,
    IonSpinner,
    IonText,
    useIonRouter,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonGrid,
} from '@ionic/react';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import {
    searchOutline,
    calendarOutline,
    personOutline,
    businessOutline,
    documentOutline,
    chevronBack,
    chevronForward,
    peopleOutline,
    constructOutline,
    checkmarkCircleOutline,
    pricetagOutline,
    barChartOutline,
} from 'ionicons/icons';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import styles from './SearchModal.module.scss';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';

interface SearchResult {
    id: string;
    type: 'booking' | 'client' | 'service' | 'staff' | 'resource' | 'task' | 'label' | 'page';
    title: string;
    subtitle?: string;
    route: string;
    icon: string;
}

interface SearchResults {
    bookings: SearchResult[];
    clients: SearchResult[];
    services: SearchResult[];
    staff: SearchResult[];
    resources: SearchResult[];
    tasks: SearchResult[];
    labels: SearchResult[];
    pages: SearchResult[];
}

interface SearchModalProps {
    isOpen?: boolean;
    onClose: () => void;

}

const SearchModal: React.FC<SearchModalProps> = ({
    isOpen,
    onClose,
}) => {
  const { t: tr } = useTranslation();
    const router = useIonRouter();
    const [searchText, setSearchText] = useState('');
    const [results, setResults] = useState<SearchResults>({
        bookings: [],
        clients: [],
        services: [],
        staff: [],
        resources: [],
        tasks: [],
        labels: [],
        pages: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
    const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
    const gridRef = useRef<HTMLIonGridElement>(null);
    const floatingBarRef = useRef<HTMLDivElement>(null);

    // Keyboard tracking: move floating search bar above keyboard
    useEffect(() => {
        const floatingBarEl = floatingBarRef.current;
        let didShowHandle: { remove: () => Promise<void> } | null = null;
        let didHideHandle: { remove: () => Promise<void> } | null = null;
        let offsetAnimRaf = 0;
        let currentOffset = 0;

        const setOverlayOffset = (offset: number) => {
            const el = floatingBarRef.current;
            if (!el) return;
            if (offset > 0) {
                el.style.setProperty('--overlay-bottom', `${offset}px`);
            } else {
                el.style.removeProperty('--overlay-bottom');
            }
        };

        const animateKeyboardOffset = (kbHeight: number) => {
            const targetOffset = kbHeight > 0 ? kbHeight + KEYBOARD_GAP_PX : 0;
            if (offsetAnimRaf) cancelAnimationFrame(offsetAnimRaf);

            const startOffset = currentOffset;
            const delta = targetOffset - startOffset;
            if (Math.abs(delta) < 1) {
                currentOffset = targetOffset;
                setOverlayOffset(currentOffset);
                return;
            }

            const duration = targetOffset > startOffset ? 220 : 170;
            const startTime = performance.now();

            const step = (now: number) => {
                const t = Math.min((now - startTime) / duration, 1);
                const eased = 1 - Math.pow(1 - t, 3);
                currentOffset = startOffset + (delta * eased);
                setOverlayOffset(currentOffset);

                if (t < 1) {
                    offsetAnimRaf = requestAnimationFrame(step);
                } else {
                    currentOffset = targetOffset;
                    setOverlayOffset(currentOffset);
                }
            };

            offsetAnimRaf = requestAnimationFrame(step);
        };

        try {
            Keyboard.addListener('keyboardWillShow', (info) => {
                animateKeyboardOffset(info.keyboardHeight || 0);
            }).then(handle => { didShowHandle = handle; }).catch(() => { });

            Keyboard.addListener('keyboardDidShow', (info) => {
                animateKeyboardOffset(info.keyboardHeight || 0);
            }).catch(() => { });

            Keyboard.addListener('keyboardWillHide', () => {
                animateKeyboardOffset(0);
            }).then(handle => { didHideHandle = handle; }).catch(() => { });

            Keyboard.addListener('keyboardDidHide', () => {
                animateKeyboardOffset(0);
            }).catch(() => { });
        } catch { }

        const vv = window.visualViewport;
        let vvRaf = 0;
        const handleViewportResize = () => {
            if (vvRaf) cancelAnimationFrame(vvRaf);
            vvRaf = requestAnimationFrame(() => {
                if (!vv) return;
                const offset = window.innerHeight - vv.height - vv.offsetTop;
                animateKeyboardOffset(offset > 50 ? offset : 0);
            });
        };
        if (vv) vv.addEventListener('resize', handleViewportResize);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') setTimeout(handleViewportResize, 300);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            const el = floatingBarEl;
            if (el) el.style.removeProperty('--overlay-bottom');
            if (offsetAnimRaf) cancelAnimationFrame(offsetAnimRaf);
            if (vvRaf) cancelAnimationFrame(vvRaf);
            if (vv) vv.removeEventListener('resize', handleViewportResize);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            try { didShowHandle?.remove(); } catch { }
            try { didHideHandle?.remove(); } catch { }
        };
    }, []);

    // Clearing results when the search text is emptied is computed during
    // render (React's "adjust state when a dependency changes" pattern)
    // rather than as a setState call inside the effect below, which now only
    // handles the actual async search call.
    const [prevSearchText, setPrevSearchText] = useState(searchText);
    if (prevSearchText !== searchText) {
        setPrevSearchText(searchText);
        if (searchText.trim().length === 0) {
            setResults({
                bookings: [],
                clients: [],
                services: [],
                staff: [],
                resources: [],
                tasks: [],
                labels: [],
                pages: []
            });
        }
    }

    const applyProgress = useCallback((progress: number, _smooth: boolean) => {
        baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
        if (titleCardAnimationRef.current) {
            titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
            titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
        }
    }, []);

    const handleScroll = useScrollSnap(gridRef, 66, applyProgress);

    const performSearch = useCallback(async (query: string) => {
        setIsLoading(true);

        try {
            const dataService = DataServiceFactory.getService();
            const searchResults: SearchResults = {
                bookings: [],
                clients: [],
                services: [],
                staff: [],
                resources: [],
                tasks: [],
                labels: [],
                pages: []
            };

            // Search contacts - enhanced with more fields (client-side filtering like ContactsList page)
            try {
                const allContacts = await dataService.contacts.getAllContacts();
                const queryLower = query.toLowerCase();

                // Filter contacts client-side like ContactsList does
                const filteredContacts = allContacts.filter(contact => {
                    const name = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
                    const displayName = (contact.displayName || '').toLowerCase();
                    const email = (contact.primaryEmail || '').toLowerCase();
                    const phone = (contact.primaryPhone || '').toLowerCase();

                    return (
                        name.includes(queryLower) ||
                        displayName.includes(queryLower) ||
                        email.includes(queryLower) ||
                        phone.includes(queryLower)
                    );
                });

                filteredContacts.slice(0, 10).forEach(contact => { // Limit to 10 results for search modal
                    const fullName = `${contact.firstName} ${contact.lastName}`.trim() || tr('components.SearchModal.unnamedClient');
                    const company = contact.company || '';
                    const email = contact.primaryEmail || '';
                    const phone = contact.primaryPhone || '';
                    const address = '';

                    // Search in name, company, email, phone, address
                    const searchableText = `${fullName} ${company} ${email} ${phone} ${address}`.toLowerCase();

                    if (searchableText.includes(query.toLowerCase())) {
                        searchResults.clients.push({
                            id: contact.id || contact._id || '',
                            type: 'client',
                            title: fullName,
                            subtitle: company || (email ? tr('components.SearchModal.hasEmail') : tr('components.SearchModal.noEmail')),
                            route: `/clients/${contact.id || contact._id}`,
                            icon: personOutline,
                        });
                    }
                });
            } catch (error) {
                console.error('Error searching contacts:', error);
            }

            // Search bookings - enhanced with notes and status
            try {
                const bookings = await dataService.bookings.getAllBookings();
                bookings.forEach(booking => {
                    const contactName = booking.contact?.name || 'Unknown';
                    const serviceType = booking.serviceType || 'Service';
                    const status = booking.status || 'pending';
                    const notes = booking.notes || '';
                    const location = booking.location || '';

                    const title = `${contactName} - ${serviceType}`;
                    const dateStr = booking.date || tr('components.SearchModal.noDate');
                    const timeStr = typeof booking.startTime === 'string' ? booking.startTime : booking.startTime?.toLocaleTimeString(intlLocale()) || tr('components.SearchModal.noTime');
                    const subtitle = tr('components.SearchModal.at', { dateStr, timeStr, status });

                    // Search in title, notes, location, status
                    const searchableText = `${title} ${notes} ${location} ${status}`.toLowerCase();

                    if (searchableText.includes(query.toLowerCase())) {
                        searchResults.bookings.push({
                            id: booking.id,
                            type: 'booking',
                            title,
                            subtitle,
                            route: `/bookings/${booking.id}`,
                            icon: calendarOutline,
                        });
                    }
                });
            } catch (error) {
                console.error('Error searching bookings:', error);
            }

            // Search services - enhanced with descriptions
            try {
                const serviceTypes = await dataService.getServiceTypes();
                const queryLower = query.toLowerCase();

                serviceTypes.forEach(service => {
                    const title = service.type || tr('components.SearchModal.unnamedService');
                    const description = tr('components.SearchModal.professionalCleaningService');

                    // Show all services for service-related searches, or if title/description matches
                    const isServiceRelated = queryLower.includes('service') || queryLower.includes('services') ||
                                           queryLower.includes('clean') || queryLower.includes('cleaning');
                    const matchesQuery = title.toLowerCase().includes(queryLower) || description.toLowerCase().includes(queryLower);

                    if (isServiceRelated || matchesQuery) {
                        searchResults.services.push({
                            id: service.serviceId || service.type,
                            type: 'service',
                            title,
                            subtitle: description,
                            route: `/services/${service.serviceId || service.type}`,
                            icon: documentOutline,
                        });
                    }
                });
            } catch (error) {
                console.error('Error searching services:', error);
            }

            // Search staff members
            try {
                const staffMembers = await dataService.staff.getAllStaffMembers();
                staffMembers.forEach(staff => {
                    const name = staff.name || tr('components.SearchModal.unnamedStaff');
                    const email = staff.email || '';
                    const phone = staff.phone || '';

                    const searchableText = `${name} ${email} ${phone}`.toLowerCase();

                    if (searchableText.includes(query.toLowerCase())) {
                        searchResults.staff.push({
                            id: staff._id || '',
                            type: 'staff',
                            title: name,
                            subtitle: email || tr('components.SearchModal.staffMember'),
                            route: `/staff/${staff._id}`,
                            icon: peopleOutline,
                        });
                    }
                });
            } catch (error) {
                console.error('Error searching staff:', error);
            }

            // Search resources
            try {
                const resources = await dataService.resources.getAllResources();
                resources.forEach(resource => {
                    const name = resource.name || tr('components.SearchModal.unnamedResource');
                    const type = resource.type || '';

                    const searchableText = `${name} ${type}`.toLowerCase();

                    if (searchableText.includes(query.toLowerCase())) {
                        searchResults.resources.push({
                            id: resource._id || '',
                            type: 'resource',
                            title: name,
                            subtitle: type || tr('components.SearchModal.equipmentResource'),
                            route: `/resources/${resource._id}`,
                            icon: constructOutline,
                        });
                    }
                });
            } catch (error) {
                console.error('Error searching resources:', error);
            }

            // Search tasks - get all contacts and their tasks
            try {
                const contacts = await dataService.contacts.getAllContacts();
                for (const contact of contacts) {
                    try {
                        const contactTasks = await dataService.tasks.getContactTasks(contact.id || contact._id || '');
                        contactTasks.forEach(task => {
                            const taskTitle = task.title || tr('components.SearchModal.untitledTask');
                            const taskNotes = task.notes || '';
                            const contactName = `${contact.firstName} ${contact.lastName}`.trim();

                            const searchableText = `${taskTitle} ${taskNotes} ${contactName}`.toLowerCase();

                            if (searchableText.includes(query.toLowerCase())) {
                                searchResults.tasks.push({
                                    id: task.id,
                                    type: 'task',
                                    title: taskTitle,
                                    subtitle: tr('components.SearchModal.for', { contactName }),
                                    route: `/clients/${contact.id || contact._id}/tasks/${task.id}`,
                                    icon: checkmarkCircleOutline,
                                });
                            }
                        });
                    } catch (taskError) {
                        console.error('Error searching tasks for contact:', contact.id, taskError);
                    }
                }
            } catch (error) {
                console.error('Error searching tasks:', error);
            }

            // Search labels
            try {
                const labels = await dataService.labels.getAllLabels();
                labels.forEach(label => {
                    const name = label.name || tr('components.SearchModal.unnamedLabel');

                    if (name.toLowerCase().includes(query.toLowerCase())) {
                        searchResults.labels.push({
                            id: label.id || label._id || '',
                            type: 'label',
                            title: name,
                            subtitle: tr('components.SearchModal.contactLabel'),
                            route: `/labels/${label.id || label._id}`,
                            icon: pricetagOutline,
                        });
                    }
                });
            } catch (error) {
                console.error('Error searching labels:', error);
            }

            // Static pages and settings - can be filtered by role later
            const pages: SearchResult[] = [
                {
                    id: 'dashboard',
                    type: 'page',
                    title: tr('components.SearchModal.dashboard'),
                    subtitle: tr('components.SearchModal.mainDashboardPage'),
                    route: '/dashboard',
                    icon: businessOutline,
                },
                {
                    id: 'bookings',
                    type: 'page',
                    title: tr('components.SearchModal.bookings'),
                    subtitle: tr('components.SearchModal.manageAllBookings'),
                    route: '/bookings',
                    icon: calendarOutline,
                },
                {
                    id: 'clients',
                    type: 'page',
                    title: tr('components.SearchModal.clients'),
                    subtitle: tr('components.SearchModal.manageClientContacts'),
                    route: '/clients',
                    icon: personOutline,
                },
                {
                    id: 'leads',
                    type: 'page',
                    title: tr('components.SearchModal.leads'),
                    subtitle: tr('components.SearchModal.manageLeadsAndProspects'),
                    route: '/leads',
                    icon: peopleOutline,
                },
                {
                    id: 'analytics',
                    type: 'page',
                    title: tr('components.SearchModal.analytics'),
                    subtitle: tr('components.SearchModal.businessAnalyticsAndReports'),
                    route: '/analytics',
                    icon: barChartOutline,
                },
            ];
            pages.forEach(page => {
                if (page.title.toLowerCase().includes(query.toLowerCase()) || (page.subtitle && page.subtitle.toLowerCase().includes(query.toLowerCase()))) {
                    searchResults.pages.push(page);
                }
            });

            setResults(searchResults);
            console.log('Final search results:', searchResults); // Debug log
        } catch (error) {
            console.error('Search error:', error);
            setResults({
                bookings: [],
                clients: [],
                services: [],
                staff: [],
                resources: [],
                tasks: [],
                labels: [],
                pages: []
            });
        } finally {
            setIsLoading(false);
        }
    }, [tr]);

    useEffect(() => {
        if (searchText.trim().length > 0) {
            Promise.resolve().then(() => performSearch(searchText));
        } else {
            // Scroll to top and reset header animation
            if (gridRef.current) {
                gridRef.current.scrollTop = 0;
                handleScroll(0);
            }
        }
    }, [searchText, handleScroll, performSearch]);

    const handleResultClick = (result: SearchResult) => {
        router.push(result.route);
        onClose();
        setSearchText('');
        setResults({
            bookings: [],
            clients: [],
            services: [],
            staff: [],
            resources: [],
            tasks: [],
            labels: [],
            pages: []
        });
    };

    const getResultIcon = (type: SearchResult['type']) => {
        switch (type) {
            case 'booking':
                return calendarOutline;
            case 'client':
                return personOutline;
            case 'service':
                return documentOutline;
            case 'staff':
                return peopleOutline;
            case 'resource':
                return constructOutline;
            case 'task':
                return checkmarkCircleOutline;
            case 'label':
                return pricetagOutline;
            case 'page':
                return businessOutline;
            default:
                return searchOutline;
        }
    };

    const getResultColor = (type: SearchResult['type']) => {
        switch (type) {
            case 'booking':
                return 'primary';
            case 'client':
                return 'secondary';
            case 'service':
                return 'tertiary';
            case 'staff':
                return 'success';
            case 'resource':
                return 'warning';
            case 'task':
                return 'danger';
            case 'label':
                return 'medium';
            case 'page':
                return 'dark';
            default:
                return 'medium';
        }
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            className={styles.searchModal}
            backdropDismiss={true}
            animated={true}
        >
            <div className={styles.modalWrapper}>
            <BasePageWithHeader
                ref={baseHeaderRef}
                title={tr('components.SearchModal.search')}
                toolbarClassName={styles.toolbar}
                smallTitleClassName={styles.smallTitle}
                titleHeaderClassName={styles.titleHeader}
                startButtons={
                    <IonButton shape='round' className={styles.backButton} onClick={onClose}>
                        <IonIcon icon={chevronBack} />
                    </IonButton>
                }
                endButtons={
                    <IonButton shape='round' className={styles.backButton} style={{ visibility: 'hidden' }}>
                        <IonIcon icon={chevronBack} />
                    </IonButton>
                }
            >
                <IonContent scrollY={false} className={styles.content}>
                    <IonGrid
                        ref={gridRef}
                        className={styles.gridContainer}
                        onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
                    >
                        <TitleCard
                            ref={titleCardAnimationRef}
                            title={tr('components.SearchModal.search')}
                            cardClassName={styles.searchTitleCard}
                            titleContainerClassName={styles.largeTitleSection}
                            titleClassName={styles.scoreTitle}
                        />

                        {searchText.trim().length === 0 && (
                            <IonCard className={styles.searchSuggestions}>
                                <IonCardHeader>
                                    <IonCardTitle className={styles.sectionTitle}>
                                        <IonIcon icon={searchOutline} className={styles.sectionIcon} />{tr('components.SearchModal.searchSuggestions')}
                                    </IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    <IonList>
                                        <IonItem button detail={false} onClick={() => setSearchText('john')} className={styles.settingsItem}>
                                            <IonIcon icon={personOutline} slot="start" />
                                            <IonLabel>
                                                <h3>{tr('components.SearchModal.searchForJohn')}</h3>
                                                <p>{tr('components.SearchModal.findClientsWithThisName')}</p>
                                            </IonLabel>
                                        </IonItem>
                                        <IonItem button detail={false} onClick={() => setSearchText('cleaning')} className={styles.settingsItem}>
                                            <IonIcon icon={documentOutline} slot="start" />
                                            <IonLabel>
                                                <h3>{tr('components.SearchModal.searchForCleaning')}</h3>
                                                <p>{tr('components.SearchModal.findCleaningServices')}</p>
                                            </IonLabel>
                                        </IonItem>
                                        <IonItem lines='none' button detail={false} onClick={() => setSearchText('tomorrow')} className={styles.settingsItem}>
                                            <IonIcon icon={calendarOutline} slot="start" />
                                            <IonLabel>
                                                <h3>{tr('components.SearchModal.searchForTomorrow')}</h3>
                                                <p>{tr('components.SearchModal.findBookingsScheduledForTomorrow')}</p>
                                            </IonLabel>
                                        </IonItem>
                                    </IonList>
                                </IonCardContent>
                            </IonCard>
                        )}

                        <div className={styles.searchContainer}>
                            {isLoading && (
                                <div className={styles.loadingContainer}>
                                    <IonSpinner name="bubbles" color="primary" />
                                    <IonText color="medium">{tr('components.SearchModal.searching')}</IonText>
                                </div>
                            )}

                            {!isLoading && searchText.trim().length > 0 && results.bookings.length === 0 && results.clients.length === 0 && results.services.length === 0 && results.staff.length === 0 && results.resources.length === 0 && results.tasks.length === 0 && results.labels.length === 0 && results.pages.length === 0 && (
                                <div className={styles.noResults}>
                                    <IonIcon icon={searchOutline} size="large" color="medium" />
                                    <IonText color="medium">
                                        <h3>{tr('components.SearchModal.noResultsFound')}</h3>
                                        <p>{tr('components.SearchModal.trySearchingForBookingsClientsServices')}</p>
                                    </IonText>
                                </div>
                            )}

                            {!isLoading && (results.bookings.length > 0 || results.clients.length > 0 || results.services.length > 0 || results.staff.length > 0 || results.resources.length > 0 || results.tasks.length > 0 || results.labels.length > 0 || results.pages.length > 0) && (
                                <div className={styles.searchSuggestions}>
                                    {results.bookings.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.bookings')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.bookings.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}

                                    {results.clients.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.clients')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.clients.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}

                                    {results.services.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.services')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.services.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}

                                    {results.staff.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.staff')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.staff.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}

                                    {results.resources.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.resources')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.resources.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}

                                    {results.tasks.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.tasks')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.tasks.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}

                                    {results.labels.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.labels')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.labels.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}

                                    {results.pages.length > 0 && (
                                        <div className={styles.suggestionSection}>
                                            <IonCardHeader className={styles.sectionHeader}>
                                                <IonCardTitle>{tr('components.SearchModal.pages')}</IonCardTitle>
                                            </IonCardHeader>
                                            <IonList className={styles.resultsList}>
                                                {results.pages.map((result) => (
                                                    <IonItem
                                                        key={result.id}
                                                        button
                                                        detail
                                                        detailIcon={chevronForward}
                                                        onClick={() => handleResultClick(result)}
                                                        className={styles.resultItem}
                                                    >
                                                        <IonIcon
                                                            icon={getResultIcon(result.type)}
                                                            slot="start"
                                                            color={getResultColor(result.type)}
                                                        />
                                                        <IonLabel>
                                                            <h3>{result.title}</h3>
                                                            {result.subtitle && <p>{result.subtitle}</p>}
                                                        </IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </IonGrid>
                </IonContent>

                {/* Floating search bar at bottom */}
                <div
                    ref={floatingBarRef}
                    className={styles.floatingSearchBar}
                >
                    <IonSearchbar
                        value={searchText}
                        onIonInput={(e) => setSearchText(e.detail.value!)}
                        placeholder={tr('components.SearchModal.searchBookingsClientsServices')}
                        className={styles.searchBar}
                        showCancelButton="never"
                        animated
                    />
                </div>
            </BasePageWithHeader>
            </div>
        </IonModal>
    );
};

export default SearchModal;
